'use client';

import { FC, useState, useRef, useEffect, useContext } from 'react';
import { useUserProfile } from '../../../../context/user-profile-context';
import { useTranslation } from '../../../../hooks/use-translation';
import GlobalContext from '../../../../context/global-context';
import SpriteIcon from '../../../shared/sprite-icon';
import OWLink from '../../../shared/ow-link';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { useDeepLink } from '../../../../hooks/use-deep-link';
import { useOverwolfStore } from '../../../../hooks/use-overwolf-store';
import { useInstalledExtensions } from '../../../../hooks/use-installed-extensions';
import { useCurrentAppId } from '../../../../hooks/use-current-app-id';
import useShowMyAccount from '../../../../hooks/use-show-my-account';
import { dispatch } from '../../../../hooks/use-bus';
import { AppBusEventType } from '../../../../interfaces/bus-events';
import { OwApp } from '../../../../interfaces/domain/ow-app.modal';
import './user-settings-menu.scss';

// -----------------------------------------------------------------------------
const UserSettingsMenu: FC = () => {
  const { currentUser, logout, isLoggedIn } = useUserProfile();
  const { config } = useContext(GlobalContext);
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const { openSettings } = useDeepLink();
  const {
    isAvailable: isOverwolfStoreAvailable,
    openSettings: openSettingsViaStore,
  } = useOverwolfStore();
  const { installedAppIds } = useInstalledExtensions();

  const [isOpen, setIsOpen] = useState(false);
  const currentAppId = useCurrentAppId();
  const showMyAccount = useShowMyAccount();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!isLoggedIn || !currentUser?.profile) {
    return null;
  }

  const { avatar, email, username, nickname } = currentUser.profile;
  const supportUrl = config?.publicRuntimeConfig?.base?.supportBaseUrl;

  const toggleMenu = (): void => {
    setIsOpen(!isOpen);
  };

  const getInitials = (): string => {
    const name = nickname || username || email || '';
    return name.charAt(0).toUpperCase();
  };

  const handleLogout = (): void => {
    setIsOpen(false);
    analytics.track('appstore_logout_clicked', {
      source: 'page header',
    });
    logout();
  };

  const isCurrentAppInstalled =
    !!currentAppId && installedAppIds.has(currentAppId);

  const handleOpenOverwolfSettings = async () => {
    setIsOpen(false);

    // Inside the Overwolf client, ask the shell to open settings over the store
    // bridge (reliable native navigation to overwolf://settings). On the web
    // there's no shell, so fall back to the deep link — and if that didn't
    // resolve (client not installed), prompt the download modal.
    if (isOverwolfStoreAvailable) {
      openSettingsViaStore('games-overlay');
      return;
    }

    const result = await openSettings('games-overlay');
    if (result.linkType !== 'deepLink') {
      dispatch({
        type: AppBusEventType.App.ShowDownloadModal,
        data: {
          isOpen: true,
          currentApp: { id: currentAppId, isElectron: false } as OwApp,
          url: null,
          isDeepLink: false,
        },
      });
    }
  };

  return (
    <div className="user-settings-menu-container" ref={menuRef}>
      <button
        className="avatar-trigger"
        onClick={toggleMenu}
        aria-label="User settings"
        aria-expanded={isOpen}
      >
        {avatar ? (
          <img src={avatar} alt={username || 'User Avatar'} />
        ) : (
          <div className="initials">{getInitials()}</div>
        )}
      </button>

      {isOpen && (
        <div className="settings-dropdown">
          <div className="user-info">
            {/* In the Overwolf client getCurrentUser has no email, so fall back
                to the display name (nickname). */}
            <span className="user-email" title={email || nickname}>
              {email || nickname}
            </span>
          </div>

          <ul className="menu-items">
            {showMyAccount && (
              <li className="menu-item">
                <OWLink href="/my-account" onClick={() => setIsOpen(false)}>
                  My Account
                </OWLink>
              </li>
            )}

            <li className="menu-item">
              <a
                href={supportUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                Support
              </a>
            </li>

            <li className="menu-item">
              <a
                href={config?.publicRuntimeConfig?.links?.social?.discord}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                Discord
              </a>
            </li>

            {isCurrentAppInstalled && (
              <li className="menu-item">
                <button onClick={handleOpenOverwolfSettings}>
                  {/* <SpriteIcon icon="cog" svgClassName="icon" /> */}
                  {t('Settings')}
                </button>
              </li>
            )}

            <li className="menu-item logout-item">
              <button onClick={handleLogout}>
                <SpriteIcon icon="logout" svgClassName="icon" />
                Log out
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default UserSettingsMenu;
